import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { FileText } from 'lucide-react';
import { api, ApiError } from '@client/lib/api';
import { currentLocale } from '@client/lib/i18n';
import { useTurnstile } from '@client/lib/use-turnstile';
import { Button } from '@client/components/ui/button';
import { Field, Input } from '@client/components/ui/input';
import { Spinner } from '@client/components/ui/feedback';
import { MarkWhite, Wordmark } from '@client/components/brand/logo';
import { LangSwitch } from '@client/components/lang-switch';
import { pickLocale, type Localised } from '@shared/types';

export const Route = createFileRoute('/s/$slug')({ component: SharePage });

interface ShareInfo { status: 'active' | 'expired'; title?: Localised; page_count?: number | null; access_days?: number; has_access?: boolean; item_id?: string | null }

/** The fixed link staff send out: name and email, then a confirmation email opens the document. A confirmed visitor goes straight through. */
function SharePage() {
  const { slug } = Route.useParams();
  const { t } = useTranslation();
  const info = useQuery({ queryKey: ['share', slug], queryFn: () => api<ShareInfo>(`/api/share/${slug}`), retry: false });
  const opening = !!info.data?.has_access && !!info.data.item_id;

  React.useEffect(() => {
    if (opening) window.location.replace(`/items/${info.data!.item_id}`);
  }, [opening, info.data]);

  return (
    <div className="grid min-h-dvh grid-cols-1 md:grid-cols-[44%_56%]">
      <section className="hero-navy relative flex flex-col justify-between p-8 text-white md:p-12">
        <div className="flex items-center gap-3">
          <MarkWhite size={36} />
          <span className="text-[15px] font-[700] tracking-[0.02em]">ResilientX</span>
        </div>
        {info.data?.title ? (
          <div className="my-16 max-w-md">
            <p className="eyebrow mb-3 text-navy-muted"><FileText className="mr-1.5 inline size-3.5" />{t('share.eyebrow')}</p>
            <h1 className="text-[32px] font-[700] leading-[38px] tracking-[-0.03em] text-white md:text-[38px] md:leading-[44px]">{pickLocale(info.data.title, currentLocale())}</h1>
            {info.data.page_count ? <p className="mt-3 text-[15px] text-navy-muted">{t('share.pages', { count: info.data.page_count })}</p> : null}
          </div>
        ) : <div />}
        <p className="max-w-md text-[13px] leading-5 text-navy-muted">{t('share.transparency')}</p>
      </section>
      <section className="relative flex items-center justify-center bg-card px-6 py-12 md:px-12">
        <div className="absolute right-6 top-6"><LangSwitch /></div>
        <div className="w-full max-w-[400px]">
          <Wordmark width={150} className="mb-10" />
          {info.isPending || opening ? <Spinner label={t('common.loading')} /> : info.isError || !info.data ? (
            <Unavailable title={t('share.unavailableTitle')} body={info.error instanceof ApiError && info.error.status === 404 ? t('share.unavailableBody') : t('errors.network')} />
          ) : info.data.status === 'expired' ? (
            <Unavailable title={t('share.expiredTitle')} body={t('share.expiredBody')} />
          ) : <RequestForm slug={slug} accessDays={info.data.access_days ?? 7} />}
        </div>
      </section>
    </div>
  );
}

function Unavailable({ title, body }: { title: string; body: string }) {
  return <div role="alert"><h2 className="page-title">{title}</h2><p className="mt-3 text-[16px] leading-6 text-body">{body}</p></div>;
}

function RequestForm({ slug, accessDays }: { slug: string; accessDays: number }) {
  const { t } = useTranslation();
  const [first, setFirst] = React.useState('');
  const [last, setLast] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [errors, setErrors] = React.useState<{ first?: string; last?: string; email?: string; form?: string }>({});
  const [sentTo, setSentTo] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const { ref: turnstileRef, token: turnstileToken, siteKey } = useTurnstile();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim().toLowerCase();
    const next: typeof errors = {};
    if (!first.trim()) next.first = t('share.required');
    if (!last.trim()) next.last = t('share.required');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) next.email = t('login.invalid');
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    setBusy(true);
    try {
      await api(`/api/share/${slug}/request`, { body: { first_name: first.trim(), last_name: last.trim(), email: value, locale: currentLocale(), turnstile_token: turnstileToken } });
      setSentTo(value);
    } catch {
      setErrors({ form: t('errors.network') });
    } finally {
      setBusy(false);
    }
  };

  if (sentTo) {
    return (
      <div>
        <h2 className="page-title">{t('login.checkInbox')}</h2>
        <p className="mt-3 text-[16px] leading-6 text-body">{t('share.sentTo', { email: sentTo })}</p>
        <Button variant="ghost" className="mt-6 -ml-3" onClick={() => setSentTo(null)}>{t('login.useAnother')}</Button>
      </div>
    );
  }
  return (
    <form onSubmit={submit} noValidate>
      <h2 className="page-title">{t('share.title')}</h2>
      <p className="mb-6 mt-3 text-[16px] leading-6 text-body">{t('share.subtitle')}</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label={t('share.firstName')} htmlFor="first" error={errors.first}><Input id="first" autoComplete="given-name" autoFocus value={first} onChange={(e) => setFirst(e.target.value)} className="h-11 text-[15px]" /></Field>
        <Field label={t('share.lastName')} htmlFor="last" error={errors.last}><Input id="last" autoComplete="family-name" value={last} onChange={(e) => setLast(e.target.value)} className="h-11 text-[15px]" /></Field>
      </div>
      <Field label={t('share.workEmail')} htmlFor="email" error={errors.email ?? errors.form}><Input id="email" type="email" inputMode="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@firm.com" className="h-11 text-[15px]" /></Field>
      {siteKey ? <div ref={turnstileRef} className="mb-4 min-h-[65px]" /> : null}
      <Button type="submit" size="lg" className="w-full" loading={busy}>{busy ? t('login.sending') : t('share.submit')}</Button>
      <p className="mt-4 text-[13px] leading-5 text-muted">{t('share.accessNote', { count: accessDays })}</p>
    </form>
  );
}
